// src/pages/RedliningViewer.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Paper, 
  IconButton, 
  Tooltip, 
  Alert, 
  Snackbar,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack,
  ZoomIn,
  ZoomOut,
  ChevronLeft,
  ChevronRight,
  AutoFixHigh,
  InsertDriveFile,
  Close,
  ErrorOutline,
  CheckCircleOutline,
  WarningAmber,
  Assignment
} from '@mui/icons-material';
import { documentService } from '../services/documentService';
import redliningApi from '../api/redlining';

// Initialize PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const RedliningViewer = () => {
  const { documentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const pdfContainerRef = useRef(null);
  
  // Document state
  const [document, setDocument] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Redlining state
  const [annotations, setAnnotations] = useState([]);
  const [selectedAnnotation, setSelectedAnnotation] = useState(null);
  const [scale, setScale] = useState(1.0);
  const [analysisDrawerOpen, setAnalysisDrawerOpen] = useState(false);
  const [analysisInProgress, setAnalysisInProgress] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [summary, setSummary] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  
  // PDF dimensions for correct positioning
  const [pdfDimensions, setPdfDimensions] = useState({ width: 0, height: 0 });
  
  // Load document and annotations
  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        // Use getDocument instead of getDocumentById
        const docResponse = await documentService.getDocument(documentId);
        setDocument(docResponse);
        
        // Set PDF URL
        const fileUrl = await documentService.getDocumentFileUrl(documentId);
        setPdfUrl(fileUrl);
        
        // Try to fetch existing annotations
        try {
          // Update to use the proper endpoint path
          const annotationsResponse = await redliningApi.getAnnotations(documentId);
          setAnnotations(annotationsResponse.data || []);
        } catch (annotationError) {
          console.error('Error fetching annotations:', annotationError);
          setAnnotations([]);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching document:', err);
        setError('Failed to load document. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [documentId]);

  // Update PDF dimensions when page or scale changes
  useEffect(() => {
    if (pdfContainerRef.current) {
      const updateDimensions = () => {
        const pdfContainer = pdfContainerRef.current.querySelector('.react-pdf__Page');
        if (pdfContainer) {
          const { width, height } = pdfContainer.getBoundingClientRect();
          setPdfDimensions({ width, height });
        }
      };
      
      // Small delay to ensure PDF has rendered
      const timer = setTimeout(updateDimensions, 500);
      
      return () => clearTimeout(timer);
    }
  }, [pageNumber, scale, pdfUrl]);

  // AI analysis function
  const handleAnalyzeContractClauses = async () => {
    try {
      setAnalysisInProgress(true);
      setNotification({
        open: true,
        message: 'AI contract analysis started. This may take a moment...',
        severity: 'info'
      });
      
      // Fix: Use the correct endpoint for analyzing contract clauses
      const response = await redliningApi.analyzeContractClauses(documentId);
      
      // Add new annotations to the state
      if (response.data.annotations && response.data.annotations.length > 0) {
        setAnnotations(response.data.annotations);
        setNotification({
          open: true,
          message: `AI analysis complete. Found ${response.data.annotations.length} clauses.`,
          severity: 'success'
        });
        // Open the analysis drawer to show results
        setAnalysisDrawerOpen(true);
      } else {
        setNotification({
          open: true,
          message: 'AI analysis complete. No issues found.',
          severity: 'info'
        });
      }
    } catch (err) {
      console.error('Error analyzing contract clauses:', err);
      setNotification({
        open: true,
        message: 'Failed to analyze contract. Please try again.',
        severity: 'error'
      });
    } finally {
      setAnalysisInProgress(false);
    }
  };

  // Generate summary
  const handleGenerateSummary = async () => {
    try {
      setLoadingSummary(true);
      setSummaryDialogOpen(true);
      
      const response = await redliningApi.generateCommentSummary(documentId);
      setSummary(response.data.summary);
    } catch (err) {
      console.error('Error generating summary:', err);
      setNotification({
        open: true,
        message: 'Failed to generate summary',
        severity: 'error'
      });
      setSummaryDialogOpen(false);
    } finally {
      setLoadingSummary(false);
    }
  };

  // Handle annotation selection
  const handleAnnotationClick = (annotation) => {
    setSelectedAnnotation(annotation);
    setAnalysisDrawerOpen(true);
  };

  // Document load handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setLoading(false);
  };

  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setError('Failed to load PDF document. Please try again.');
    setLoading(false);
  };

  // Navigation handlers
  const prevPage = () => {
    setPageNumber(pageNumber <= 1 ? 1 : pageNumber - 1);
  };

  const nextPage = () => {
    setPageNumber(pageNumber >= numPages ? numPages : pageNumber + 1);
  };

  // Zoom handlers
  const zoomIn = () => {
    setScale(scale + 0.1);
  };

  const zoomOut = () => {
    setScale(Math.max(0.5, scale - 0.1));
  };

  // Get current page annotations
  const getCurrentPageAnnotations = () => {
    return annotations.filter(a => a.page === pageNumber);
  };

  // Calculate highlight positions based on PDF dimensions and annotation data
  const getHighlightPosition = (annotation) => {
    // In a real implementation, these positions would come from annotation coordinates
    // For this example, we'll spread them vertically with some randomization
    const index = annotations.findIndex(a => a.id === annotation.id);
    
    // This is placeholder logic - in a real app, you'd use the actual text positions from the PDF
    const topPercentage = 10 + (index % 5) * 15 + Math.random() * 5;
    const leftPercentage = 10 + (index % 3) * 25;
    
    // Add some randomness to the width to simulate different text lengths
    const widthPercentage = 20 + Math.random() * 40;
    
    return {
      top: `${topPercentage}%`,
      left: `${leftPercentage}%`,
      width: `${widthPercentage}%`,
      height: '30px',
    };
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
        <Button 
          variant="outlined" 
          startIcon={<ArrowBack />} 
          onClick={() => navigate('/redlining')} 
          sx={{ mt: 2 }}
        >
          Back to Documents
        </Button>
      </Box>
    );
  }

  // Filter annotations for current page
  const pageAnnotations = annotations.filter(a => a.page === pageNumber);

  return (
    <Box height="100vh" display="flex" flexDirection="column">
      {/* Header Toolbar */}
      <Paper 
        elevation={2} 
        sx={{ 
          p: 1.5, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          zIndex: 10
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => navigate('/redlining')}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" noWrap>
            {document?.name || 'Document Viewer'}
          </Typography>
        </Box>
        
        <Box display="flex" gap={1} alignItems="center">
          <Tooltip title="Previous Page">
            <span> {/* Wrap disabled button in span */}
              <IconButton 
                onClick={prevPage} 
                disabled={pageNumber <= 1}
              >
                <ChevronLeft />
              </IconButton>
            </span>
          </Tooltip>
          
          <Typography>
            Page {pageNumber} of {numPages || '?'}
          </Typography>
          
          <Tooltip title="Next Page">
            <span> {/* Wrap disabled button in span */}
              <IconButton 
                onClick={nextPage} 
                disabled={pageNumber >= numPages}
              >
                <ChevronRight />
              </IconButton>
            </span>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <Tooltip title="Zoom Out">
            <span> {/* Wrap disabled button in span */}
              <IconButton onClick={zoomOut} disabled={scale <= 0.5}>
                <ZoomOut />
              </IconButton>
            </span>
          </Tooltip>
          
          <Typography sx={{ minWidth: '40px', textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Typography>
          
          <Tooltip title="Zoom In">
            <IconButton onClick={zoomIn}>
              <ZoomIn />
            </IconButton>
          </Tooltip>
          
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AutoFixHigh />}
            onClick={handleAnalyzeContractClauses}
            disabled={analysisInProgress}
          >
            {analysisInProgress ? 'Analyzing...' : 'Analyze Contract'}
          </Button>
          
          <Button 
            variant="outlined" 
            color="secondary" 
            startIcon={<Assignment />}
            onClick={handleGenerateSummary}
            disabled={annotations.length === 0}
          >
            Summary
          </Button>
        </Box>
      </Paper>
      
      {/* Main Content */}
      <Box 
        display="flex" 
        flex={1} 
        overflow="hidden" 
        position="relative"
      >
        {/* Document viewer */}
        <Box 
          ref={containerRef}
          flex={1} 
          position="relative" 
          overflow="auto" 
          bgcolor="#f5f5f5"
        >
          {/* PDF Document with Reference for Positioning */}
          <Box 
            ref={pdfContainerRef}
            position="relative" 
            display="flex" 
            justifyContent="center" 
            p={2}
          >
            {pdfUrl && (
              <Document
                file={pdfUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<CircularProgress />}
              >
                <Box position="relative">
                  {/* Highlight overlay - positioned correctly relative to the PDF */}
                  <Box position="absolute" top={0} left={0} width="100%" height="100%" style={{ pointerEvents: 'none' }}>
                    {pageAnnotations.map((annotation, index) => {
                      const { content } = annotation;
                      const highlightColor = content.color || 
                        (content.riskLevel === 'HIGH' ? '#FF0000' : 
                         content.riskLevel === 'MEDIUM' ? '#FFCC00' : 
                         '#00CC00');
                      
                      const position = getHighlightPosition(annotation);
                      
                      return (
                        <Box
                          key={annotation.id}
                          position="absolute"
                          sx={{
                            ...position,
                            bgcolor: `${highlightColor}50`,
                            border: `2px solid ${highlightColor}`,
                            borderRadius: "2px",
                            p: 1,
                            cursor: 'pointer',
                            pointerEvents: 'auto',
                            zIndex: 10,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.75rem',
                            fontWeight: 'bold',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => handleAnnotationClick(annotation)}
                        >
                          {content.text?.substring(0, 15) || 'Clause'}...
                        </Box>
                      );
                    })}
                  </Box>
                  <Page 
                    pageNumber={pageNumber} 
                    scale={scale}
                    renderTextLayer={true}
                    renderAnnotationLayer={false}
                  />
                </Box>
              </Document>
            )}
          </Box>
        </Box>
        
        {/* Analysis Results Drawer */}
        <Drawer
          anchor="right"
          open={analysisDrawerOpen}
          onClose={() => setAnalysisDrawerOpen(false)}
          sx={{ width: 360, flexShrink: 0, '& .MuiDrawer-paper': { width: 360 } }}
        >
          <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Contract Analysis</Typography>
              <IconButton onClick={() => setAnalysisDrawerOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            {selectedAnnotation ? (
              <Box mb={3}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Selected Clause
                </Typography>
                
                <Paper elevation={1} sx={{ p: 2, mt: 1, bgcolor: '#f9f9f9' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Chip 
                      icon={
                        selectedAnnotation.content.riskLevel === 'HIGH' ? <ErrorOutline /> :
                        selectedAnnotation.content.riskLevel === 'MEDIUM' ? <WarningAmber /> :
                        <CheckCircleOutline />
                      }
                      label={selectedAnnotation.content.riskLevel || 'UNKNOWN'} 
                      size="small"
                      color={
                        selectedAnnotation.content.riskLevel === 'HIGH' ? 'error' :
                        selectedAnnotation.content.riskLevel === 'MEDIUM' ? 'warning' :
                        'success'
                      }
                    />
                    <Typography variant="caption">Page {selectedAnnotation.page}</Typography>
                  </Box>
                  
                  <Box 
                    sx={{ 
                      p: 1, 
                      borderRadius: 1,
                      fontSize: '0.9rem',
                      mb: 2,
                      border: '1px solid #e0e0e0',
                      background: '#fff',
                      maxHeight: '150px',
                      overflowY: 'auto'
                    }}
                  >
                    <Typography variant="body2">
                      "{selectedAnnotation.content.text}"
                    </Typography>
                  </Box>
                  
                  {selectedAnnotation.content.explanation && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Analysis:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedAnnotation.content.explanation}
                      </Typography>
                    </Box>
                  )}
                  
                  {selectedAnnotation.content.suggestion && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" fontWeight="bold" color="primary">
                        Suggestion:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {selectedAnnotation.content.suggestion}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Select a highlighted clause to see details
              </Typography>
            )}
            
            <Typography variant="subtitle1" fontWeight="bold">
              All Issues {getCurrentPageAnnotations().length > 0 && `(Page ${pageNumber})`}
            </Typography>
            
            {annotations.length === 0 ? (
              <Box textAlign="center" py={3}>
                <Typography variant="body2" color="textSecondary">
                  No analysis results yet
                </Typography>
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AutoFixHigh />}
                  onClick={handleAnalyzeContractClauses}
                  disabled={analysisInProgress}
                  sx={{ mt: 2 }}
                >
                  {analysisInProgress ? 'Analyzing...' : 'Analyze Now'}
                </Button>
              </Box>
            ) : getCurrentPageAnnotations().length === 0 ? (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                No issues found on this page
              </Typography>
            ) : (
              <List sx={{ mt: 1 }}>
                {getCurrentPageAnnotations().map(annotation => (
                  <ListItem 
                    key={annotation.id} 
                    button
                    selected={selectedAnnotation?.id === annotation.id}
                    onClick={() => setSelectedAnnotation(annotation)}
                    sx={{ 
                      mb: 1,
                      borderRadius: 1,
                      bgcolor: selectedAnnotation?.id === annotation.id ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
                      borderLeft: `4px solid ${
                        annotation.content.riskLevel === 'HIGH' ? '#FF0000' :
                        annotation.content.riskLevel === 'MEDIUM' ? '#FFCC00' :
                        '#00CC00'
                      }`
                    }}
                  >
                    <ListItemIcon>
                      {annotation.content.riskLevel === 'HIGH' ? (
                        <ErrorOutline color="error" />
                      ) : annotation.content.riskLevel === 'MEDIUM' ? (
                        <WarningAmber color="warning" />
                      ) : (
                        <CheckCircleOutline color="success" />
                      )}
                    </ListItemIcon>
                    <ListItemText 
                      primary={
                        annotation.content.text 
                          ? (annotation.content.text.length > 50 
                              ? `${annotation.content.text.substring(0, 50)}...` 
                              : annotation.content.text)
                          : 'Unnamed clause'
                      }
                      secondary={`Risk: ${annotation.content.riskLevel || 'Unknown'}`}
                    />
                  </ListItem>
                ))}
              </List>
            )}
            
            {annotations.length > 0 && (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={handleGenerateSummary}
                sx={{ mt: 2 }}
              >
                Generate Summary Report
              </Button>
            )}
          </Box>
        </Drawer>
      </Box>
      
      {/* Summary Dialog */}
      <Dialog
        open={summaryDialogOpen}
        onClose={() => setSummaryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Contract Analysis Summary</DialogTitle>
        
        <DialogContent>
          {loadingSummary ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper elevation={1} sx={{ p: 3, bgcolor: '#f9f9f9' }}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {summary || 'No summary available. Try analyzing the contract first.'}
              </Typography>
            </Paper>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setSummaryDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      
      {/* Notifications */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RedliningViewer;